import { useEffect, useState } from 'react';
import { games, type JournalDto } from '../net/api';
import { Sheet } from './Sheet';
import { Thumb } from './Thumb';
import type { AvatarLook } from '../art/avatar';

const TABS = ['Challenges', 'Achievements', 'Collections', 'Leaderboards'] as const;
const reward = (r: { coins?: number; xp?: number; candy?: number }) => [r.coins ? `${r.coins} coins` : '', r.candy ? `${r.candy} candy` : '', r.xp ? `${r.xp} XP` : ''].filter(Boolean).join(' + ');

export function Journal({ onClose, me, look, brand }: { onClose: () => void; me: string; look: AvatarLook; brand?: 'bubble' | 'bomb' }) {
  // Players aged 8–14 know Bomb Squad as Bubble Squad.
  const label = (n: string) => (brand === 'bubble' ? n.replace(/Bomb Squad( \(Bubble Squad\))?/g, 'Bubble Squad') : n.replace(' (Bubble Squad)', ''));
  const [tab, setTab] = useState<(typeof TABS)[number]>('Challenges');
  const [j, setJ] = useState<JournalDto>();
  const [boards, setBoards] = useState<{ key: string; name: string; unit: string }[]>([]);
  const [board, setBoard] = useState<{ name: string; unit: string; rows: { rank: number; displayName: string; value: number }[] }>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    games.journal().then(setJ, (e: Error) => setError(e.message));
    games.boards().then((b) => {
      setBoards(b.boards);
      if (b.boards[0]) void games.board(b.boards[0].key).then(setBoard);
    }, () => {});
  }, []);

  return (
    <Sheet title="Journal" subtitle="Challenges, achievements, collections and leaderboards" onClose={onClose} wide>
      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'tab on' : 'tab'} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>
      {error && <p className="form-error">{error}</p>}
      {j && tab === 'Challenges' && (
        <div className="stack">
          {(['event', 'daily', 'weekly'] as const).filter((p) => j.challenges.some((c) => c.period === p)).map((period) => (
            <section key={period} className="stack">
              <h3 className="mini-head">{period === 'event' ? 'Festival challenges' : period === 'daily' ? "Today's challenges" : "This week's challenges"}</h3>
              {j.challenges.filter((c) => c.period === period).map((c) => (
                <div key={c.key} className={c.completed ? 'goal done' : 'goal'}>
                  <div className="goal-text"><strong>{c.name}</strong><span className="muted small-print">Reward: {reward(c.reward)}</span></div>
                  <Progress value={c.progress} goal={c.goal} done={c.completed} />
                </div>
              ))}
            </section>
          ))}
          <p className="muted small-print">Daily challenges change at midnight UTC; weekly ones on Monday.</p>
        </div>
      )}
      {j && tab === 'Achievements' && (
        <div className="ach-grid">
          {j.achievements.map((a) => (
            <article key={a.key} className={a.unlockedAt ? 'ach on' : 'ach'}>
              <strong>{label(a.name)}</strong>
              <span className="muted small-print">{label(a.description)}</span>
              {a.unlockedAt ? <span className="tag">Unlocked</span> : <Progress value={a.progress} goal={a.goal} />}
            </article>
          ))}
          {j.hiddenRemaining > 0 && <article className="ach secret"><strong>??? × {j.hiddenRemaining}</strong><span className="muted small-print">Secret achievements. Keep exploring!</span></article>}
        </div>
      )}
      {j && tab === 'Collections' && (
        <div className="stack">
          {j.collections.map((c) => (
            <section key={c.key} className="collection">
              <div className="row-wrap"><h3 className="mini-head">{c.name}</h3><span className="tag">{c.found} / {c.total} found</span></div>
              <p className="muted small-print">{c.description} Complete it for {reward(c.reward)}.</p>
              <div className="col-items">
                {c.items.map((it, i) => (
                  <div key={i} className={it.found ? `col-item rarity-${it.rarity}` : 'col-item missing'} title={it.name}>
                    {it.found ? <Thumb item={{ type: it.type, slot: it.slot, render: it.render }} look={look} size={48} /> : <span className="q">?</span>}
                    <span className="small-print">{it.name}</span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      {tab === 'Leaderboards' && (
        <div className="stack">
          <div className="chip-row">
            {boards.map((b) => (
              <button key={b.key} className={board?.name === b.name ? 'chip on' : 'chip'} onClick={() => void games.board(b.key).then(setBoard)}>{label(b.name)}</button>
            ))}
          </div>
          {board && (
            board.rows.length === 0 ? <p className="muted">No scores yet. Be the first!</p> : (
              <ol className="board">
                {board.rows.map((r) => (
                  <li key={r.rank} className={r.displayName === me ? 'me' : ''}><span className="rank">{r.rank}</span><span>{r.displayName}</span><span className="val">{r.value.toLocaleString()} {board.unit}</span></li>
                ))}
              </ol>
            )
          )}
        </div>
      )}
    </Sheet>
  );
}

function Progress({ value, goal, done }: { value: number; goal: number; done?: boolean }) {
  const pct = Math.min(100, Math.round((value / goal) * 100));
  return (
    <div className="progress" aria-label={`${value} of ${goal}`}>
      <span className="bar"><i style={{ width: `${pct}%` }} /></span>
      <span className="small-print">{done ? 'Done!' : `${value.toLocaleString()} / ${goal.toLocaleString()}`}</span>
    </div>
  );
}
